import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, FileText, Search, Info,
  ChevronDown, ChevronUp, Clock, Sparkles, Eye, Edit3, Check, Lock, Download
} from 'lucide-react';
import { usePlan } from '../context/PlanContext';
import { addPdfHeader, addPdfFooterToAllPages, ensureImagesLoaded } from '../utils/pdfBranding';

import API from '../utils/api';

var SCORE_COLORS = function (v) {
  if (v >= 70) return '#10B981';
  if (v >= 40) return '#EAB308';
  return '#EF4444';
};

function PlanAnalysis() {
  var [step, setStep] = useState('intake');
  var [text, setText] = useState('');
  var [acknowledged, setAcknowledged] = useState(false);
  var [scanning, setScanning] = useState(false);
  var [scanResult, setScanResult] = useState(null);
  var [analyzing, setAnalyzing] = useState(false);
  var [analysis, setAnalysis] = useState(null);
  var [analysisId, setAnalysisId] = useState(null);
  var [history, setHistory] = useState([]);
  var [viewingHistory, setViewingHistory] = useState(null);
  var [confirming, setConfirming] = useState(false);
  var [editSummary, setEditSummary] = useState(false);
  var [editedSummary, setEditedSummary] = useState('');
  var [limitError, setLimitError] = useState(null);
  var { plan, getUsage } = usePlan();

  var loadHistory = useCallback(function () {
    axios.get(API + '/plan-analysis/history', { withCredentials: true })
      .then(function (r) { setHistory(r.data); })
      .catch(function () {});
  }, []);

  useEffect(function () { loadHistory(); }, [loadHistory]);

  function handleScan() {
    if (!text.trim() || !acknowledged) return;
    setScanning(true);
    setScanResult(null);
    axios.post(API + '/plan-analysis/scan', { text: text, acknowledged: true }, { withCredentials: true })
      .then(function (r) {
        setScanResult(r.data);
        if (!r.data.has_sensitive_content) {
          setStep('clean');
        } else {
          setStep('flagged');
        }
      })
      .catch(function (e) { alert(e.response ? e.response.data.detail : 'Scan failed'); })
      .finally(function () { setScanning(false); });
  }

  function handleAutoSanitize() {
    if (!scanResult || !scanResult.flags.length) return;
    axios.post(API + '/plan-analysis/sanitize', { text: text, flags: scanResult.flags }, { withCredentials: true })
      .then(function (r) {
        setText(r.data.sanitized_text);
        if (r.data.clean) {
          setStep('clean');
          setScanResult({ has_sensitive_content: false, flags: [], flag_count: 0, categories_found: [] });
        } else {
          setScanResult({ has_sensitive_content: true, flags: r.data.remaining_flags, flag_count: r.data.remaining_flags.length, categories_found: r.data.remaining_flags.map(function (f) { return f.category; }) });
        }
      })
      .catch(function () {});
  }

  function handleAnalyze() {
    setAnalyzing(true);
    setLimitError(null);
    axios.post(API + '/plan-analysis/analyze', { text: text, acknowledged: true }, { withCredentials: true })
      .then(function (r) {
        setAnalysis(r.data.analysis);
        setAnalysisId(r.data.id);
        setEditedSummary(r.data.analysis.summary || '');
        setStep('review');
        loadHistory();
      })
      .catch(function (e) {
        if (e.response && (e.response.status === 429 || e.response.status === 403)) {
          setLimitError(e.response.data.detail);
          setStep('limit');
        } else {
          alert(e.response ? e.response.data.detail : 'Analysis failed');
        }
      })
      .finally(function () { setAnalyzing(false); });
  }

  function handleConfirm(designation) {
    if (!analysisId) return;
    setConfirming(true);
    var edits = {};
    if (editSummary && editedSummary !== analysis.summary) {
      edits.summary = editedSummary;
    }
    axios.post(API + '/plan-analysis/' + analysisId + '/confirm', { edits: edits, designation: designation || 'proposed' }, { withCredentials: true })
      .then(function () {
        loadHistory();
        setStep('confirmed');
      })
      .catch(function () {})
      .finally(function () { setConfirming(false); });
  }

  function handleReject() {
    if (!analysisId) return;
    axios.post(API + '/plan-analysis/' + analysisId + '/reject', {}, { withCredentials: true })
      .then(function () {
        loadHistory();
        resetForm();
      })
      .catch(function () {});
  }

  function handleDesignation(id, designation) {
    axios.patch(API + '/plan-analysis/' + id + '/designation', { designation: designation }, { withCredentials: true })
      .then(function () { loadHistory(); })
      .catch(function (e) { alert(e.response ? e.response.data.detail : 'Failed'); });
  }

  function handleDeleteHistory(id) {
    if (!window.confirm('Delete this analysis?')) return;
    axios.delete(API + '/plan-analysis/' + id, { withCredentials: true })
      .then(function () { loadHistory(); if (viewingHistory && viewingHistory.id === id) setViewingHistory(null); })
      .catch(function () {});
  }

  function resetForm() {
    setStep('intake');
    setText('');
    setAcknowledged(false);
    setScanResult(null);
    setAnalysis(null);
    setAnalysisId(null);
    setEditSummary(false);
    setEditedSummary('');
  }

  async function exportAnalysisPDF(a, designation) {
    await ensureImagesLoaded();
    var pdf = new jsPDF();
    pdf.setFont('times', 'normal');
    var pw = pdf.internal.pageSize.getWidth();
    var ph = pdf.internal.pageSize.getHeight();
    var margin = 15;
    var contentW = pw - margin * 2;
    var footerZone = 22;
    var y = addPdfHeader(pdf);

    function checkPage(need) { if (y > ph - footerZone - (need || 10)) { pdf.addPage(); y = addPdfHeader(pdf); } }

    // Title
    pdf.setFont('times', 'bold'); pdf.setFontSize(14); pdf.setTextColor(0, 0, 0);
    pdf.text('Parenting Plan Analysis Report', pw / 2, y, { align: 'center' });
    y += 6;
    if (designation) {
      pdf.setFontSize(10); pdf.setFont('times', 'italic');
      pdf.text('Designation: ' + designation.charAt(0).toUpperCase() + designation.slice(1), pw / 2, y, { align: 'center' });
      y += 5;
    }
    pdf.setFont('times', 'normal'); pdf.setFontSize(8); pdf.setTextColor(80, 80, 80);
    pdf.text('Generated: ' + new Date().toLocaleString(), pw / 2, y, { align: 'center' });
    y += 8;

    // Scores Table
    var scores = a.scores || {};
    var scoreKeys = [
      { key: 'clarity', label: 'Clarity' },
      { key: 'completeness', label: 'Completeness' },
      { key: 'specificity', label: 'Specificity' },
      { key: 'cooperation_structure', label: 'Cooperation' },
      { key: 'conflict_risk', label: 'Conflict Risk' },
    ];
    checkPage(20);
    pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
    pdf.text('SCORES', margin, y); y += 5;
    var colW = contentW / 5;
    scoreKeys.forEach(function(s, i) {
      var val = scores[s.key] || 0;
      var r, g, b;
      if (s.key === 'conflict_risk') { if (val > 60) { r=239;g=68;b=68; } else if (val > 30) { r=234;g=179;b=8; } else { r=16;g=185;b=129; } }
      else { if (val >= 70) { r=16;g=185;b=129; } else if (val >= 40) { r=234;g=179;b=8; } else { r=239;g=68;b=68; } }
      var cx = margin + i * colW;
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3);
      pdf.rect(cx, y, colW, 14);
      pdf.setFont('times', 'normal'); pdf.setFontSize(7); pdf.setTextColor(80, 80, 80);
      pdf.text(s.label, cx + colW / 2, y + 4, { align: 'center' });
      pdf.setFont('times', 'bold'); pdf.setFontSize(14); pdf.setTextColor(r, g, b);
      pdf.text(String(val), cx + colW / 2, y + 11, { align: 'center' });
    });
    y += 18;

    // Helper to write colored section
    function writeSection(title, color, items, renderItem) {
      checkPage(14);
      var cr = parseInt(color.slice(1, 3), 16); var cg = parseInt(color.slice(3, 5), 16); var cb = parseInt(color.slice(5, 7), 16);
      pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(cr, cg, cb);
      pdf.text(title, margin, y); y += 5;
      pdf.setDrawColor(cr, cg, cb); pdf.setLineWidth(0.3); pdf.line(margin, y, pw - margin, y); y += 4;
      items.forEach(function(item) {
        checkPage(12);
        renderItem(item, cr, cg, cb);
      });
      y += 3;
    }

    // Summary
    if (a.summary) {
      checkPage(14);
      pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
      pdf.text('SUMMARY', margin, y); y += 5;
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3); pdf.line(margin, y, pw - margin, y); y += 4;
      pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
      var sumLines = pdf.splitTextToSize(a.summary, contentW);
      sumLines.forEach(function(line) { checkPage(7); pdf.text(line, margin, y); y += 7; });
      y += 4;
    }

    // Ambiguous Clauses
    if (a.ambiguous_clauses && a.ambiguous_clauses.length > 0) {
      writeSection('AMBIGUOUS CLAUSES (' + a.ambiguous_clauses.length + ')', '#EAB308', a.ambiguous_clauses, function(c, cr, cg, cb) {
        pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(cr, cg, cb);
        pdf.text(c.concept || '', margin + 2, y); y += 7;
        pdf.setFont('times', 'italic'); pdf.setFontSize(10); pdf.setTextColor(80, 80, 80);
        var clauseLines = pdf.splitTextToSize('"' + (c.clause || '') + '"', contentW - 4);
        clauseLines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
        var riskLines = pdf.splitTextToSize(c.risk || '', contentW - 4);
        riskLines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(6, 182, 212);
        var guidLines = pdf.splitTextToSize(c.guidance || '', contentW - 4);
        guidLines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        y += 3;
      });
    }

    // Missing Clauses
    if (a.missing_clauses && a.missing_clauses.length > 0) {
      writeSection('MISSING CLAUSES (' + a.missing_clauses.length + ')', '#EF4444', a.missing_clauses, function(c, cr, cg, cb) {
        pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(cr, cg, cb);
        pdf.text(c.topic || '', margin + 2, y); y += 7;
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
        var expLines = pdf.splitTextToSize(c.explanation || '', contentW - 4);
        expLines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        y += 3;
      });
    }

    // Conflict Risk Markers
    if (a.conflict_risk_markers && a.conflict_risk_markers.length > 0) {
      writeSection('CONFLICT RISK AREAS', '#5A4E9E', a.conflict_risk_markers, function(c) {
        var rColor = c.risk_level === 'high' ? '#EF4444' : c.risk_level === 'medium' ? '#EAB308' : '#10B981';
        var rr = parseInt(rColor.slice(1, 3), 16); var rg = parseInt(rColor.slice(3, 5), 16); var rb = parseInt(rColor.slice(5, 7), 16);
        pdf.setFont('times', 'bold'); pdf.setFontSize(10); pdf.setTextColor(rr, rg, rb);
        pdf.text(c.area || '', margin + 2, y); y += 7;
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(100, 100, 100);
        var lines = pdf.splitTextToSize(c.explanation || '', contentW - 4);
        lines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        y += 3;
      });
    }

    // Structured Plan
    if (a.structured_plan) {
      checkPage(14);
      pdf.setFont('times', 'bold'); pdf.setFontSize(11); pdf.setTextColor(0, 0, 0);
      pdf.text('EXTRACTED PLAN STRUCTURE', margin, y); y += 5;
      pdf.setDrawColor(0, 0, 0); pdf.setLineWidth(0.3); pdf.line(margin, y, pw - margin, y); y += 4;
      Object.entries(a.structured_plan).forEach(function(entry) {
        if (!entry[1]) return;
        checkPage(10);
        pdf.setFont('times', 'bold'); pdf.setFontSize(9); pdf.setTextColor(100, 100, 100);
        pdf.text(entry[0].replace(/_/g, ' ').toUpperCase(), margin + 2, y); y += 5;
        pdf.setFont('times', 'normal'); pdf.setFontSize(10); pdf.setTextColor(0, 0, 0);
        var lines = pdf.splitTextToSize(String(entry[1]), contentW - 4);
        lines.forEach(function(l) { checkPage(7); pdf.text(l, margin + 2, y); y += 7; });
        y += 3;
      });
    }

    addPdfFooterToAllPages(pdf);
    pdf.save('parentum-plan-analysis.pdf');
  }


  // Render analysis results (reused for both live + history)
  function renderAnalysis(a) {
    if (!a) return null;
    var scores = a.scores || {};
    return (
      <div className="space-y-5">
        {/* Scores */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { key: 'clarity', label: 'Clarity' },
            { key: 'completeness', label: 'Completeness' },
            { key: 'specificity', label: 'Specificity' },
            { key: 'cooperation_structure', label: 'Cooperation' },
            { key: 'conflict_risk', label: 'Conflict Risk' },
          ].map(function (s) {
            var val = scores[s.key] || 0;
            var color = s.key === 'conflict_risk' ? (val > 60 ? '#EF4444' : val > 30 ? '#EAB308' : '#10B981') : SCORE_COLORS(val);
            return (
              <div key={s.key} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-3 text-center" data-testid={'score-' + s.key}>
                <p className="text-xs text-white/40 uppercase mb-1">{s.label}</p>
                <p className="text-2xl font-bold" style={{ color: color }}>{val}</p>
              </div>
            );
          })}
        </div>

        {/* Summary */}
        <div className="card-surface p-5">
          <h4 className="text-sm font-semibold text-white/70 mb-2 flex items-center gap-2">
            <FileText className="w-4 h-4" /> Summary
          </h4>
          <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap">{a.summary}</p>
        </div>

        {/* Ambiguous Clauses */}
        {a.ambiguous_clauses && a.ambiguous_clauses.length > 0 && (
          <div className="card-surface p-5">
            <h4 className="text-sm font-semibold text-[#EAB308] mb-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" /> Ambiguous Clauses ({a.ambiguous_clauses.length})
            </h4>
            <div className="space-y-3">
              {a.ambiguous_clauses.map(function (c, i) {
                return (
                  <div key={i} className="p-3 bg-[#EAB308]/5 border border-[#EAB308]/15 rounded-lg" data-testid={'ambiguous-' + i}>
                    <p className="text-xs text-[#EAB308] font-medium mb-1">{c.concept}</p>
                    <p className="text-xs text-white/50 italic mb-1.5">"{c.clause}"</p>
                    <p className="text-xs text-white/40">{c.risk}</p>
                    <p className="text-xs text-[#5A4E9E] mt-1">{c.guidance}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Missing Clauses */}
        {a.missing_clauses && a.missing_clauses.length > 0 && (
          <div className="card-surface p-5">
            <h4 className="text-sm font-semibold text-[#EF4444] mb-3 flex items-center gap-2">
              <XCircle className="w-4 h-4" /> Missing Clauses ({a.missing_clauses.length})
            </h4>
            <div className="space-y-2">
              {a.missing_clauses.map(function (c, i) {
                return (
                  <div key={i} className="p-3 bg-[#EF4444]/5 border border-[#EF4444]/15 rounded-lg" data-testid={'missing-' + i}>
                    <p className="text-xs text-[#EF4444] font-medium mb-1">{c.topic}</p>
                    <p className="text-xs text-white/40">{c.explanation}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Conflict Risk Markers */}
        {a.conflict_risk_markers && a.conflict_risk_markers.length > 0 && (
          <div className="card-surface p-5">
            <h4 className="text-sm font-semibold text-[#5A4E9E] mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Conflict Risk Areas
            </h4>
            <div className="space-y-2">
              {a.conflict_risk_markers.map(function (c, i) {
                var rColor = c.risk_level === 'high' ? '#EF4444' : c.risk_level === 'medium' ? '#EAB308' : '#10B981';
                return (
                  <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                    <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: rColor }}></span>
                    <div>
                      <p className="text-xs font-medium text-white/60">{c.area}</p>
                      <p className="text-xs text-white/35 mt-0.5">{c.explanation}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Structured Plan */}
        {a.structured_plan && (
          <div className="card-surface p-5">
            <h4 className="text-sm font-semibold text-white/70 mb-3 flex items-center gap-2">
              <Eye className="w-4 h-4" /> Extracted Plan Structure
            </h4>
            <div className="space-y-2">
              {Object.entries(a.structured_plan).map(function (entry) {
                var key = entry[0];
                var val = entry[1];
                if (!val) return null;
                return (
                  <div key={key} className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
                    <p className="text-xs text-white/30 uppercase tracking-wider mb-1">{key.replace(/_/g, ' ')}</p>
                    <p className="text-xs text-white/55 leading-relaxed">{val}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg">
          <p className="text-xs text-white/25 text-center">
            This analysis is for educational and organizational purposes only. Always refer to your official court order as the controlling document.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="plan-analysis-page">
      <div className="card-surface p-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <Search className="w-5 h-5 text-[#5A4E9E]" />
              <h2 className="text-2xl font-bold">Plan Analysis</h2>
            </div>
            <p className="text-white/50 text-sm">AI-powered parenting plan review with privacy-first processing</p>
          </div>
          {step !== 'intake' && (
            <button onClick={resetForm} className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white/60 hover:text-white hover:bg-white/10 transition-colors" data-testid="new-analysis-btn">
              New Analysis
            </button>
          )}
        </div>
      </div>

      {/* Usage Meter for Free Users */}
      {plan === 'free' && (function() {
        var usage = getUsage('plan_scan');
        if (!usage || usage.unlimited) return null;
        var remaining = Math.max(usage.limit - usage.used, 0);
        return (
          <div className={'border rounded-xl p-4 flex items-center justify-between ' + (remaining === 0 ? 'bg-[#EF4444]/5 border-[#EF4444]/20' : 'bg-white/[0.03] border-white/[0.06]')} data-testid="scan-usage-meter">
            <div className="flex items-center gap-3">
              {remaining === 0 ? <Lock className="w-4 h-4 text-[#EF4444]" /> : <Search className="w-4 h-4 text-[#5A4E9E]" />}
              <div>
                <p className="text-sm font-medium">{remaining === 0 ? 'Free scan used — upgrade to analyze more plans' : 'Free Plan: ' + remaining + ' scan remaining this month'}</p>
                <p className="text-xs text-white/30">Resets on the 1st of each month.</p>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Step 1: Intake */}
      {step === 'intake' && (
        <div className="card-surface p-6 space-y-5" data-testid="intake-step">
          <div className="p-4 bg-[#F59E0B]/10 border border-[#F59E0B]/20 rounded-xl" data-testid="privacy-warning">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-[#F59E0B] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[#F59E0B] font-semibold mb-2">Important Privacy Notice</p>
                <p className="text-xs text-white/50 leading-relaxed mb-2">
                  Before pasting your parenting plan, please remove any of the following:
                </p>
                <ul className="text-xs text-white/40 space-y-1 list-disc list-inside mb-3">
                  <li>Protected health information and medical diagnoses</li>
                  <li>Social Security numbers</li>
                  <li>Bank or financial account numbers</li>
                  <li>Sealed or confidential court language</li>
                  <li>Legally protected or restricted text</li>
                </ul>
                <p className="text-xs text-white/40">Our system will automatically scan for sensitive content, but you should remove it first for maximum protection.</p>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-xs text-white/40 mb-2">Paste your parenting plan text below</label>
            <textarea value={text} onChange={function (e) { setText(e.target.value); }}
              className="w-full h-48 bg-white/[0.03] border border-white/[0.08] text-white text-sm rounded-xl px-4 py-3 resize-y focus:outline-none focus:border-[#5A4E9E]/40 leading-relaxed"
              placeholder="Paste the text of your parenting plan here..."
              data-testid="plan-text-input" />
            <p className="text-xs text-white/25 mt-1">{text.length.toLocaleString()} characters</p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer" data-testid="acknowledge-checkbox">
            <input type="checkbox" checked={acknowledged} onChange={function () { setAcknowledged(!acknowledged); }}
              className="mt-1 w-4 h-4 rounded border-white/20 bg-white/5 accent-[#5A4E9E]" />
            <span className="text-xs text-white/50 leading-relaxed">
              I confirm I have removed protected health information, sensitive personal information, shielded financial records,
              sealed/confidential court language, and other restricted content before analysis.
            </span>
          </label>

          <button onClick={handleScan} disabled={scanning || !text.trim() || !acknowledged}
            className="w-full py-3 bg-[#5A4E9E] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-30 transition-opacity flex items-center justify-center gap-2"
            data-testid="scan-btn">
            {scanning ? (
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Scanning...</>
            ) : (
              <><Shield className="w-4 h-4" /> Scan & Analyze</>
            )}
          </button>
        </div>
      )}

      {/* Master Plan Info Section */}
      {step === 'intake' && (
        <div className="card-surface p-5 space-y-4" data-testid="master-plan-info">
          <div className="flex items-center gap-2 mb-1">
            <Info className="w-4 h-4 text-[#5A4E9E]" />
            <h3 className="text-sm font-semibold text-white/70">How Master Plans Work</h3>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="bg-[#10B981]/5 border border-[#10B981]/15 rounded-lg p-3">
              <p className="text-xs font-semibold text-[#10B981] mb-1.5">Master Plan</p>
              <p className="text-xs text-white/50 leading-relaxed">Your confirmed, active parenting plan. When a plan is designated as <strong className="text-white/70">Master</strong>, the AI across Parentum uses its provisions as the source of truth. Only one plan can be Master at a time.</p>
            </div>
            <div className="bg-[#EAB308]/5 border border-[#EAB308]/15 rounded-lg p-3">
              <p className="text-xs font-semibold text-[#EAB308] mb-1.5">Proposed Plan</p>
              <p className="text-xs text-white/50 leading-relaxed">Draft or proposed plans you want analyzed but that are <strong className="text-white/70">not yet in effect</strong>. The AI will not base guidance or conflict checks on proposed plans.</p>
            </div>
          </div>

          <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 space-y-2">
            <p className="text-xs font-semibold text-white/40 uppercase tracking-wider">Where the Master Plan is used</p>
            <div className="grid gap-1.5 text-xs text-white/50">
              <div className="flex items-start gap-2">
                <span className="text-[#2D2560] mt-0.5">-</span>
                <span><strong className="text-white/70">Companion Chat</strong> — Your AI companion references the master plan provisions when giving co-parenting guidance. It will cite specific clauses and flag when a described situation may conflict with your plan.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#2D2560] mt-0.5">-</span>
                <span><strong className="text-white/70">Journal Entries</strong> — When you log an event, the AI checks if the described behavior aligns with or violates your master plan, and adds a plan reference note. Entries are also checked for grammar to ensure accurate analysis.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#2D2560] mt-0.5">-</span>
                <span><strong className="text-white/70">Calendar Conflict Analysis</strong> — The daily AI summary considers your master plan provisions when detecting scheduling conflicts and making recommendations.</span>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#2D2560] mt-0.5">-</span>
                <span><strong className="text-white/70">Communication Analysis</strong> — Message analysis takes into account what your plan says when evaluating co-parent communications.</span>
              </div>
            </div>
          </div>

          <p className="text-xs text-white/25 leading-relaxed">
            Toggle a plan between Master and Proposed at any time in the history below. Setting a new plan as Master automatically demotes the previous one.
          </p>
        </div>
      )}

      {/* Step 2: Flagged Content */}
      {step === 'flagged' && scanResult && (
        <div className="card-surface p-6 space-y-5" data-testid="flagged-step">
          <div className="p-4 bg-[#EF4444]/10 border border-[#EF4444]/20 rounded-xl">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="w-5 h-5 text-[#EF4444]" />
              <p className="text-sm text-[#EF4444] font-semibold">Sensitive Content Detected</p>
            </div>
            <p className="text-xs text-white/50">{scanResult.flag_count} item(s) flagged in categories: {scanResult.categories_found.join(', ')}</p>
          </div>

          <div className="space-y-2">
            {scanResult.flags.map(function (f, i) {
              return (
                <div key={i} className="flex items-start gap-3 p-3 bg-white/[0.02] border border-white/[0.06] rounded-lg" data-testid={'flag-' + i}>
                  <span className={'w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ' + (f.severity === 'critical' ? 'bg-[#EF4444]' : 'bg-[#EAB308]')}></span>
                  <div>
                    <p className="text-xs font-medium text-white/60">{f.description}</p>
                    <p className="text-xs text-white/30 mt-0.5">Category: {f.category} | Severity: {f.severity}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="flex gap-3">
            <button onClick={function () { setStep('intake'); }}
              className="flex-1 py-3 bg-white/5 border border-white/10 text-white/70 font-medium rounded-xl hover:bg-white/10 transition-colors"
              data-testid="edit-manually-btn">
              <Edit3 className="w-4 h-4 inline mr-2" /> Edit Manually
            </button>
            <button onClick={handleAutoSanitize}
              className="flex-1 py-3 bg-[#5A4E9E] text-white font-medium rounded-xl hover:opacity-90 transition-opacity"
              data-testid="auto-sanitize-btn">
              <Shield className="w-4 h-4 inline mr-2" /> Auto-Sanitize & Continue
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Clean - Ready to Analyze */}
      {step === 'clean' && (
        <div className="card-surface p-6 space-y-5" data-testid="clean-step">
          <div className="p-4 bg-[#10B981]/10 border border-[#10B981]/20 rounded-xl flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-[#10B981]" />
            <p className="text-sm text-[#10B981] font-medium">Content scan passed. Ready for AI analysis.</p>
          </div>
          <button onClick={handleAnalyze} disabled={analyzing}
            className="w-full py-3 bg-gradient-to-r from-[#5A4E9E] to-[#2D2560] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-30 transition-opacity flex items-center justify-center gap-2"
            data-testid="analyze-btn">
            {analyzing ? (
              <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span> Analyzing plan...</>
            ) : (
              <><Sparkles className="w-4 h-4" /> Analyze Plan</>
            )}
          </button>
        </div>
      )}

      {/* Step 4: Review */}
      {step === 'review' && analysis && (
        <div className="space-y-5" data-testid="review-step">
          <div className="p-4 bg-[#5A4E9E]/10 border border-[#5A4E9E]/20 rounded-xl">
            <p className="text-sm text-[#5A4E9E] font-medium mb-1">Review Your Analysis</p>
            <p className="text-xs text-white/40">Review the AI-generated analysis below. You may edit the summary, then confirm or reject.</p>
          </div>

          {/* Editable summary */}
          <div className="card-surface p-5">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-white/70 flex items-center gap-2"><FileText className="w-4 h-4" /> Summary</h4>
              <button onClick={function () { setEditSummary(!editSummary); }}
                className="text-xs text-[#5A4E9E] hover:underline" data-testid="toggle-edit-summary">
                {editSummary ? 'Cancel Edit' : 'Edit'}
              </button>
            </div>
            {editSummary ? (
              <textarea value={editedSummary} onChange={function (e) { setEditedSummary(e.target.value); }}
                className="w-full h-32 bg-white/[0.03] border border-white/[0.08] text-white text-sm rounded-xl px-4 py-3 resize-y focus:outline-none focus:border-[#5A4E9E]/40"
                data-testid="edit-summary-input" />
            ) : (
              <p className="text-sm text-white/60 leading-relaxed whitespace-pre-wrap">{editedSummary || analysis.summary}</p>
            )}
          </div>

          {renderAnalysis(Object.assign({}, analysis, { summary: null }))}

          <div className="flex gap-3">
            <button onClick={handleReject}
              className="flex-1 py-3 bg-white/5 border border-white/10 text-[#EF4444] font-medium rounded-xl hover:bg-[#EF4444]/10 transition-colors"
              data-testid="reject-btn">
              <XCircle className="w-4 h-4 inline mr-2" /> Reject & Start Over
            </button>
            <button onClick={function() { handleConfirm('master'); }} disabled={confirming}
              className="flex-1 py-3 bg-[#10B981] text-white font-semibold rounded-xl hover:opacity-90 disabled:opacity-30 transition-opacity"
              data-testid="confirm-master-btn">
              {confirming ? 'Confirming...' : <><Check className="w-4 h-4 inline mr-2" /> Confirm as Master</>}
            </button>
            <button onClick={function() { handleConfirm('proposed'); }} disabled={confirming}
              className="flex-1 py-3 bg-[#EAB308]/20 text-[#EAB308] border border-[#EAB308]/30 font-semibold rounded-xl hover:bg-[#EAB308]/30 disabled:opacity-30 transition-opacity"
              data-testid="confirm-proposed-btn">
              {confirming ? 'Confirming...' : <><Check className="w-4 h-4 inline mr-2" /> Confirm as Proposed</>}
            </button>
          </div>

          <button onClick={function() { exportAnalysisPDF(analysis); }}
            className="w-full py-3 bg-white/5 border border-white/10 text-white/60 font-medium rounded-xl hover:bg-white/10 transition-colors flex items-center justify-center gap-2"
            data-testid="export-analysis-pdf">
            <Download className="w-4 h-4" /> Download Analysis as PDF
          </button>
        </div>
      )}
      {step === 'limit' && (
        <div className="card-surface p-8 text-center space-y-4" data-testid="limit-step">
          <div className="w-16 h-16 rounded-full bg-[#EF4444]/10 border border-[#EF4444]/20 flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7 text-[#EF4444]" />
          </div>
          <h3 className="text-xl font-bold">Plan Scan Limit Reached</h3>
          <p className="text-sm text-white/50 max-w-md mx-auto">{limitError}</p>
          <div className="pt-2">
            <button onClick={function() { window.dispatchEvent(new CustomEvent('navigate-billing')); }}
              className="px-6 py-3 bg-gradient-to-r from-[#2D2560] to-[#5A4E9E] text-white rounded-xl font-medium hover:opacity-90 transition-opacity"
              data-testid="upgrade-from-scan-btn">
              Upgrade Your Plan
            </button>
          </div>
        </div>
      )}

      {step === 'confirmed' && (
        <div className="card-surface p-6 text-center space-y-4" data-testid="confirmed-step">
          <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto" />
          <h3 className="text-xl font-bold">Analysis Confirmed</h3>
          <p className="text-sm text-white/50">Your structured plan analysis has been saved and will be used for ongoing co-parenting insights.</p>
          <button onClick={resetForm} className="px-6 py-2.5 bg-[#5A4E9E] text-white rounded-xl font-medium hover:opacity-90 transition-opacity" data-testid="analyze-another-btn">
            Analyze Another Plan
          </button>
        </div>
      )}

      {/* History */}
      {history.length > 0 && (
        <div className="card-surface p-6" data-testid="analysis-history">
          <h3 className="text-sm font-semibold text-white/70 uppercase tracking-wider mb-4 flex items-center gap-2">
            <Clock className="w-4 h-4" /> Analysis History
          </h3>
          <div className="space-y-2">
            {history.map(function (h) {
              var isOpen = viewingHistory && viewingHistory.id === h.id;
              var statusColor = h.status === 'confirmed' ? '#10B981' : h.status === 'rejected' ? '#EF4444' : '#EAB308';
              var isMaster = h.designation === 'master';
              return (
                <div key={h.id} className={'border rounded-xl overflow-hidden ' + (isMaster ? 'border-[#10B981]/30 bg-[#10B981]/[0.03]' : 'border-white/[0.06]')}>
                  <div className="flex items-center justify-between p-3 hover:bg-white/[0.02] transition-colors">
                    <div className="flex items-center gap-3 cursor-pointer flex-1" onClick={function () { setViewingHistory(isOpen ? null : h); }}>
                      <span className="w-2 h-2 rounded-full" style={{ backgroundColor: statusColor }}></span>
                      <span className="text-sm text-white/60">{new Date(h.created_at).toLocaleDateString()}</span>
                      <span className="text-xs px-2 py-0.5 rounded-full capitalize" style={{ color: statusColor, backgroundColor: statusColor + '15' }}>{h.status}</span>
                      {isMaster ? (
                        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-[#10B981]/20 text-[#10B981] border border-[#10B981]/30" data-testid={'badge-master-' + h.id}>Master</span>
                      ) : h.status === 'confirmed' ? (
                        <span className="text-xs font-bold uppercase px-2 py-0.5 rounded-full bg-[#EAB308]/20 text-[#EAB308] border border-[#EAB308]/30" data-testid={'badge-proposed-' + h.id}>Proposed</span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {h.status === 'confirmed' && (
                        <div className="flex items-center gap-2" onClick={function(e) { e.stopPropagation(); }}>
                          <span className="text-xs text-white/30">Proposed</span>
                          <button onClick={function() { handleDesignation(h.id, isMaster ? 'proposed' : 'master'); }}
                            className={'relative w-9 h-5 rounded-full transition-colors ' + (isMaster ? 'bg-[#10B981]' : 'bg-white/15')}
                            data-testid={'toggle-master-' + h.id}>
                            <span className={'absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ' + (isMaster ? 'left-[18px]' : 'left-0.5')}></span>
                          </button>
                          <span className={'text-xs font-medium ' + (isMaster ? 'text-[#10B981]' : 'text-white/30')}>Master</span>
                        </div>
                      )}
                      <button onClick={function(e) { e.stopPropagation(); handleDeleteHistory(h.id); }}
                        className="p-1 text-red-400/40 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors">
                        <XCircle className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={function(e) { e.stopPropagation(); if (h.analysis) exportAnalysisPDF(h.analysis, h.designation); }}
                        className="p-1 text-white/20 hover:text-[#5A4E9E] hover:bg-[#5A4E9E]/10 rounded-lg transition-colors"
                        data-testid={'export-pdf-' + h.id}>
                        <Download className="w-3.5 h-3.5" />
                      </button>
                      {isOpen ? <ChevronUp className="w-4 h-4 text-white/30" /> : <ChevronDown className="w-4 h-4 text-white/30" />}
                    </div>
                  </div>
                  {isOpen && h.analysis && (
                    <div className="px-4 pb-4 pt-2 border-t border-white/[0.04]">
                      {renderAnalysis(h.analysis)}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default PlanAnalysis;
